import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import {
  findCampaignListItem,
  getCampaign,
  retryCampaign,
  sendDraft,
} from "../lib/emailApi";
import type {
  CampaignListItemDto,
  CampaignStatusResponseDto,
  EmailJobStatusDto,
  JobStatus,
} from "../types/campaign";

const JOB_STATUS_META: Record<JobStatus, { label: string; cls: string }> = {
  pending: { label: "Pending", cls: "pill--queued" },
  sending: { label: "Sending", cls: "pill--processing" },
  sent: { label: "Sent", cls: "pill--completed" },
  failed: { label: "Failed", cls: "pill--failed" },
  retrying: { label: "Retrying", cls: "pill--partial" },
};

function StatusPill({ status }: { status: JobStatus }) {
  const meta = JOB_STATUS_META[status] ?? { label: status, cls: "pill--queued" };
  return <span className={`pill ${meta.cls}`}>{meta.label}</span>;
}

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function ProgressArc({ value, total, color }: { value: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className="progress-arc">
      <strong style={{ color }}>{pct}%</strong>
      <span>
        {value} / {total}
      </span>
    </div>
  );
}

function JobTable({ jobs, filter }: { jobs: EmailJobStatusDto[]; filter?: JobStatus }) {
  const [search, setSearch] = useState("");
  const visible = jobs.filter(
    (job) =>
      (!filter || job.status === filter) &&
      (!search ||
        job.storeNumber.includes(search) ||
        job.toEmail.toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <div>
      <input
        className="search-input"
        type="search"
        placeholder="Search store number or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: "0.75rem" }}
      />
      <div className="job-table">
        <div className="job-table__head">
          <span>Store #</span>
          <span>Email</span>
          <span>Status</span>
          <span>Updated</span>
        </div>
        {visible.length === 0 && (
          <div className="store-list__empty" style={{ padding: "1rem" }}>
            No records match.
          </div>
        )}
        {visible.map((job) => (
          <div key={job.jobId} className="job-table__row">
            <span className="job-table__store">#{job.storeNumber}</span>
            <span className="job-table__email">{job.toEmail}</span>
            <span>
              <StatusPill status={job.status} />
            </span>
            <span className="job-table__date">{formatDate(job.sentAt)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

type JobFilter = "all" | JobStatus;

export default function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<CampaignStatusResponseDto | null>(null);
  const [campaignMeta, setCampaignMeta] = useState<CampaignListItemDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobFilter, setJobFilter] = useState<JobFilter>("all");
  const [retryState, setRetryState] = useState<"idle" | "retrying" | "done" | "error">("idle");
  const [retryMsg, setRetryMsg] = useState("");
  const [sendingDraft, setSendingDraft] = useState(false);

  const load = useCallback(async () => {
    if (!campaignId) return;

    setLoading(true);
    setError(null);

    try {
      const [detail, meta] = await Promise.all([
        getCampaign(campaignId),
        findCampaignListItem(campaignId),
      ]);
      setCampaign(detail);
      setCampaignMeta(meta);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load campaign.");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRetry() {
    if (!campaign || !campaignMeta) return;

    setRetryState("retrying");

    try {
      const result = await retryCampaign(campaign.campaignId, {
        subject: campaign.subject,
        body: campaignMeta.body,
      });
      setRetryMsg(`Retried ${result.retried} failed sends.`);
      setRetryState("done");
      await load();
    } catch (err) {
      setRetryMsg(err instanceof Error ? err.message : "Retry failed.");
      setRetryState("error");
    }
  }

  async function handleSendDraft() {
    if (!campaign) return;

    setSendingDraft(true);

    try {
      await sendDraft(campaign.campaignId);
      navigate("/campaigns");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send draft.");
      setSendingDraft(false);
    }
  }

  if (loading) {
    return (
      <AppShell title="Campaign detail" eyebrow="Campaign" description="" showBackLink>
        <div className="page-loading">Loading campaign…</div>
      </AppShell>
    );
  }

  if (error || !campaign) {
    return (
      <AppShell title="Campaign not found" eyebrow="Campaign" description="" showBackLink>
        <div className="status-banner status-banner--error" style={{ marginTop: "1.5rem" }}>
          {error ?? "Campaign not found."}
        </div>
        <Link
          to="/campaigns"
          className="button button--ghost"
          style={{ marginTop: "1rem", display: "inline-flex" }}
        >
          Back to campaigns
        </Link>
      </AppShell>
    );
  }

  const failedJobs = campaign.jobs.filter((job) => job.status === "failed");
  const sentJobs = campaign.jobs.filter((job) => job.status === "sent");
  const inFlightJobs = campaign.jobs.filter(
    (job) => job.status === "sending" || job.status === "retrying" || job.status === "pending",
  );
  const filteredJobs =
    jobFilter === "all" ? campaign.jobs : campaign.jobs.filter((job) => job.status === jobFilter);

  return (
    <AppShell
      title={campaign.subject || "Campaign detail"}
      eyebrow={campaignMeta?.isDraft ? "Draft" : "Campaign"}
      description={`Campaign ID: ${campaign.campaignId} · Created ${formatDate(campaign.createdAt)}`}
      showBackLink
    >
      <section className="section">
        <div className="campaign-detail__actions">
          <Link to="/campaigns" className="button button--ghost">
            ← All campaigns
          </Link>

          {campaignMeta?.isDraft && (
            <button
              className="button button--primary"
              onClick={handleSendDraft}
              disabled={sendingDraft}
            >
              {sendingDraft ? "Sending…" : "Send this draft"}
            </button>
          )}

          {!campaignMeta?.isDraft && failedJobs.length > 0 && campaignMeta && (
            <button
              className="button button--primary"
              onClick={handleRetry}
              disabled={retryState === "retrying"}
            >
              {retryState === "retrying" ? "Retrying…" : `Retry ${failedJobs.length} failed`}
            </button>
          )}

          <button className="button button--ghost" onClick={() => void load()}>
            Refresh
          </button>
        </div>

        {retryState === "done" && (
          <div className="status-banner status-banner--ok" style={{ marginTop: "1rem" }}>
            {retryMsg}
          </div>
        )}
        {retryState === "error" && (
          <div className="status-banner status-banner--error" style={{ marginTop: "1rem" }}>
            {retryMsg}
          </div>
        )}

        <div className="stats-grid" style={{ marginTop: "1.5rem" }}>
          <article className="stat-card">
            <span>Total stores</span>
            <strong>{campaign.totalCount}</strong>
            <p>{campaignMeta?.targetAll ? "All stores targeted" : "Selected stores"}</p>
          </article>
          <article className="stat-card">
            <span>Sent</span>
            <strong>{campaign.sentCount}</strong>
            <ProgressArc value={campaign.sentCount} total={campaign.totalCount} color="#2e7df6" />
          </article>
          <article className="stat-card">
            <span>Pending</span>
            <strong>{campaign.pendingCount}</strong>
            <ProgressArc value={campaign.pendingCount} total={campaign.totalCount} color="#1a7a3a" />
          </article>
          <article className="stat-card">
            <span>Failed</span>
            <strong className={campaign.failedCount > 0 ? "stat-card__warn" : ""}>
              {campaign.failedCount}
            </strong>
            <ProgressArc value={campaign.failedCount} total={campaign.totalCount} color="#e10600" />
          </article>
        </div>

        <article className="panel" style={{ marginTop: "1.5rem" }}>
          <div className="panel__header">
            <div>
              <p className="section__eyebrow">Campaign details</p>
              <h2>{campaign.subject}</h2>
            </div>
            <div style={{ display: "grid", gap: "0.5rem", textAlign: "right" }}>
              <span
                className={`pill ${
                  campaign.status === "completed"
                    ? "pill--completed"
                    : campaign.status === "failed"
                      ? "pill--failed"
                      : campaign.status === "partial_failed"
                        ? "pill--partial"
                        : "pill--queued"
                }`}
              >
                {campaignMeta?.isDraft ? "draft" : campaign.status}
              </span>
              {campaignMeta?.messageType && <span className="tag">{campaignMeta.messageType}</span>}
            </div>
          </div>

          <div className="detail-grid">
            {campaignMeta?.scheduledAt && (
              <div className="detail-item">
                <span>Scheduled for</span>
                <strong>{formatDate(campaignMeta.scheduledAt)}</strong>
              </div>
            )}
            {campaignMeta?.senderName && (
              <div className="detail-item">
                <span>Sender name</span>
                <strong>{campaignMeta.senderName}</strong>
              </div>
            )}
            {campaignMeta?.tags && campaignMeta.tags.length > 0 && (
              <div className="detail-item detail-item--full">
                <span>Tags</span>
                <div className="tag-row">
                  {campaignMeta.tags.map((tag) => (
                    <span key={tag} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {campaignMeta?.teaser && (
              <div className="detail-item detail-item--full">
                <span>Preview text</span>
                <strong>{campaignMeta.teaser}</strong>
              </div>
            )}
          </div>
        </article>

        {campaign.jobs.length > 0 && (
          <article className="panel" style={{ marginTop: "1.5rem" }}>
            <div className="panel__header">
              <div>
                <p className="section__eyebrow">Per-store delivery</p>
                <h2>Individual send status for all {campaign.jobs.length} stores</h2>
              </div>
            </div>

            <div className="tab-strip" style={{ marginTop: "1rem" }}>
              {(["all", "pending", "sending", "retrying", "sent", "failed"] as JobFilter[]).map(
                (filter) => {
                  const count =
                    filter === "all"
                      ? campaign.jobs.length
                      : campaign.jobs.filter((job) => job.status === filter).length;

                  return (
                    <button
                      key={filter}
                      className={`tab-btn${jobFilter === filter ? " tab-btn--active" : ""}`}
                      onClick={() => setJobFilter(filter)}
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                      {count > 0 && <span className="tab-badge">{count}</span>}
                    </button>
                  );
                },
              )}
            </div>

            <div className="detail-grid" style={{ marginTop: "1rem" }}>
              <div className="detail-item">
                <span>Sent jobs</span>
                <strong>{sentJobs.length}</strong>
              </div>
              <div className="detail-item">
                <span>In flight</span>
                <strong>{inFlightJobs.length}</strong>
              </div>
              <div className="detail-item">
                <span>Failed jobs</span>
                <strong>{failedJobs.length}</strong>
              </div>
            </div>

            <div style={{ marginTop: "1rem" }}>
              <JobTable jobs={filteredJobs} filter={jobFilter === "all" ? undefined : jobFilter} />
            </div>
          </article>
        )}
      </section>
    </AppShell>
  );
}
